import { type KeyboardEvent, type MouseEvent, useId, useState } from 'react';
import {
  normalizeProjectBadgeChars,
  projectBadgeColorOptions,
  resolveProjectBadgeSwatch,
} from '@/shared/data/projectBadges';
import { componentSizes } from '@/theme/componentSizes';
import { r, s } from '@/theme/tokens';
import { Box } from '@/ui';
import { FormHelperText, Menu, MenuItem, Text, TextField } from '@/ui/primitives';
import { formFieldLabelSx } from '@/ui/primitives/formFieldStyles';
import type { ProjectBadgeColor } from '@flowqa/shared';
import type { ReactElement } from 'react';

type ProjectBadgeFieldProps = {
  chars: string;
  color: ProjectBadgeColor;
  onCharsChange: (value: string) => void;
  onColorChange: (value: ProjectBadgeColor) => void;
  disabled?: boolean;
};

const swatchSize = '24px';
const swatchRadius = '6px';
const controlSize = `${componentSizes.auth.inputHeight}px`;
const colorSelectWidth = controlSize;

const swatchSx = {
  display: 'inline-flex',
  width: swatchSize,
  height: swatchSize,
  minWidth: swatchSize,
  minHeight: swatchSize,
  maxWidth: swatchSize,
  maxHeight: swatchSize,
  flex: '0 0 auto',
  boxSizing: 'border-box',
  borderRadius: swatchRadius,
  border: '1px solid',
  borderColor: 'divider',
} as const;

const projectBadgeHelperTextSx = {
  mt: s('xxs'),
  fontSize: (theme: { typography: { subtitle1: { fontSize?: string | number } } }) =>
    theme.typography.subtitle1.fontSize ?? 'inherit',
} as const;

export const ProjectBadgeField = ({
  chars,
  color,
  onCharsChange,
  onColorChange,
  disabled = false,
}: ProjectBadgeFieldProps): ReactElement => {
  const fieldId = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpenColorMenu = (event: MouseEvent<HTMLElement>): void => {
    if (disabled) return;
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseColorMenu = (): void => {
    setAnchorEl(null);
  };

  const handleColorKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (disabled) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `minmax(0, 1fr) ${colorSelectWidth}`,
        alignItems: 'center',
        columnGap: s('md'),
        width: '100%',
      }}
    >
      <Text role="body" component="label" htmlFor={fieldId} sx={formFieldLabelSx}>
        Project Badge
      </Text>
      <Box />
      <TextField
        id={fieldId}
        fieldStyle="settings"
        value={chars}
        placeholder="PR"
        slotProps={{ htmlInput: { maxLength: 2 } }}
        disabled={disabled}
        onChange={(event) => {
          onCharsChange(normalizeProjectBadgeChars(event.target.value));
        }}
        sx={{ mt: s('sm') }}
      />
      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label="Choose project badge color"
        onClick={handleOpenColorMenu}
        onKeyDown={handleColorKeyDown}
        sx={{
          minWidth: colorSelectWidth,
          width: colorSelectWidth,
          maxWidth: colorSelectWidth,
          minHeight: controlSize,
          height: controlSize,
          maxHeight: controlSize,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: r('md'),
          backgroundColor: 'background.default',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <Box
          component="span"
          sx={[swatchSx, { backgroundColor: resolveProjectBadgeSwatch(color) }]}
        />
      </Box>
      <FormHelperText sx={projectBadgeHelperTextSx}>Up to 2 characters.</FormHelperText>
      <Box />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseColorMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: colorSelectWidth,
              width: colorSelectWidth,
              maxWidth: colorSelectWidth,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: `${r('md')} !important`,
              backgroundColor: 'background.default',
              backgroundImage: 'none',
              boxShadow: 'none',
              overflow: 'hidden',
            },
          },
          list: {
            sx: {
              p: s('none'),
              m: s('none'),
              backgroundColor: 'background.default',
            },
          },
        }}
      >
        {projectBadgeColorOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => {
              onColorChange(option.value);
              handleCloseColorMenu();
            }}
            sx={{
              minHeight: `calc(${s('2xl')} + ${s('xs')})`,
              px: s('md'),
              py: s('md'),
              justifyContent: 'center',
              borderRadius: r('none'),
              backgroundColor: color === option.value ? 'action.selected' : undefined,
            }}
          >
            <Box component="span" sx={[swatchSx, { backgroundColor: option.swatch }]} />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
